import { ChevronDownIcon, InfoOutlineIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Box, Button, Checkbox, Divider, Flex, Input, InputGroup, InputRightAddon, Menu, MenuButton, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Radio, RadioGroup, Select, SkeletonText, Stack, Switch, Text, Tooltip, useColorModeValue, useToast, Collapse } from '@chakra-ui/react';
import currencySymbolMap from 'currency-symbol-map';
import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { GiStarShuriken } from "react-icons/gi";
import { useCreateCampaignMutation, useGetCataloguesQuery, useUpdateEntriesCampaignMutation } from 'store/campaigns/campaignsApi';
import * as Yup from 'yup';
import { bidStrategiesForLeads, bidStrategiesForSales, budgetOptions, salesOptions } from '../../../../utils/campaignOptions';
import moment from 'moment';
import BudgetScheduleSpecs from 'components/adspresso/BudgetScheduleSpecs';
import CatalogueSelector from 'components/adspresso/CatalogueSelector';


function CreateCampaignForm({ adsAccountData, onClose, isOpen, onSuccess }) {

    const toast = useToast();
    const background = useColorModeValue('gray.50', 'gray.800');
    const buttonTextColor = useColorModeValue('gray.800', '#fff');

    const currencyCode = adsAccountData?.currency || 'INR';
    const currencySymbol = currencySymbolMap(currencyCode);
    const [isSchedulingExpanded, setIsSchedulingExpanded] = useState(false);

    const [createCampaign] = useCreateCampaignMutation();
    const [updateEntriesCampaign, { isLoading: isCampaignEntriesUpdating }] = useUpdateEntriesCampaignMutation({});

    const formik = useFormik({
        initialValues: {
            name: '',
            objective: 'OUTCOME_LEADS',
            isAdvantageCampaignEnabled: false,
            isAdvantageCatalogueEnabled: false,
            isAdScheduled: false,
            accelerated: false,
            product_catalog_id: '',
            smart_promotion_type: 'MANUAL_LEAD',
            budget: 'daily_budget',
            bid_strategy: 'LOWEST_COST_WITHOUT_CAP',
            daily_budget: 800,
            lifetime_budget: 14000,
            is_budget_schedule_enabled: false,
            budget_schedule_specs: 
            [
                {
                  time_start: moment().add(1, 'day').startOf('day').unix(),
                  time_end: moment().add(2, 'day').startOf('day').unix(),
                  budget_value_type: 'ABSOLUTE',
                  budget_value: 5,
                },
            ],
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Campaign name is required'),
            objective: Yup.string().required('Objective is required'),
            daily_budget: Yup.number().when('budget', {
                is: 'daily_budget',
                then: schema => schema.required('Daily budget is required').min(1, 'Daily budget must be a positive number'),
                otherwise: schema => schema.notRequired(),
            }),

            lifetime_budget: Yup.number().when('budget', {
                is: 'lifetime_budget',
                then: schema => schema.required('Lifetime budget is required').min(1, 'Lifetime budget must be a positive number'),
                otherwise: schema => schema.notRequired(),
            }),
        }),
        onSubmit: (values, { setSubmitting }) => {
            const { name, objective, smart_promotion_type, accelerated, bid_strategy, budget, daily_budget, lifetime_budget, isAdScheduled, isAdvantageCampaignEnabled, is_budget_schedule_enabled, budget_schedule_specs, isAdvantageCatalogueEnabled, product_catalog_id } = values;
            const payload = {
                name,
                objective,
                currency: currencyCode,
                adsAccount: {
                    adsAccountId: adsAccountData?.account_id,
                    adsAccountName: adsAccountData?.name
                },
                special_ad_categories: ["NONE"],
            };

            if (objective === 'OUTCOME_SALES' && smart_promotion_type === 'AUTOMATED_SHOPPING_ADS') {
                payload.smart_promotion_type = smart_promotion_type;
            }

            // Only add these fields if isAdvantageCampaignEnabled is true
            if (isAdvantageCampaignEnabled) {
                payload.bid_strategy = bid_strategy;

                if (budget === 'daily_budget') {
                    payload.daily_budget = Number(daily_budget) * 100;
                    payload.currency = currencyCode;

                }

                if (budget === 'lifetime_budget') {
                    payload.lifetime_budget = Number(lifetime_budget) * 100;
                    payload.currency = currencyCode;
                }

                payload.pacing_type = [];

                // Add "no_pacing" if accelerated
                if (accelerated) {
                    payload.pacing_type.push("no_pacing");
                }

                // Add "day_parting" if ad is scheduled
                if (isAdScheduled) {
                    payload.pacing_type.push("day_parting");
                }

                // If "no_pacing" is not included, default to "standard"
                if (!payload.pacing_type.includes("no_pacing")) {
                    payload.pacing_type.push("standard");
                }

                if (is_budget_schedule_enabled) {
                    payload.is_budget_schedule_enabled = is_budget_schedule_enabled;

                    payload.budget_schedule_specs = budget_schedule_specs.map(spec => ({
                        ...spec,
                        budget_value: spec.budget_value_type === 'ABSOLUTE'
                        ? Math.round(spec.budget_value * 100) // Convert dollars to cents
                        : spec.budget_value // Leave as-is for MULTIPLIER (percentage)
                    }));
                    }
                }

            if (isAdvantageCatalogueEnabled) {
                payload.promoted_object = {
                  product_catalog_id: product_catalog_id
                };
            }  

            createCampaign({ payload, adsAccountId: adsAccountData?.account_id })
                .unwrap()
                .then((response) => {

                    updateEntriesCampaign({
                        payload: {
                            campaign: {
                                campaign_id: response?.data?.id,
                                name,
                            },
                            adsAccount: {
                                adsAccountId: adsAccountData?.account_id,
                                adsAccountName: adsAccountData?.name,
                            }
                        }
                    }).unwrap()
                        .then(() => {
                    })
                    onSuccess({ createdId: response?.data?.id })
                    toast({
                        position: "top-right",
                        title: "Campaign created successfully",
                        status: "success",
                        duration: 4000,
                        isClosable: true,
                    });
                    onClose(); // Close the modal
                })
                .catch((error) => {
                    toast({
                        position: "top-right",
                        title: "Error creating campaign",
                        status: "error",
                        description: error?.data?.message,
                        duration: 4000,
                        isClosable: true,
                    });
                    console.error('Error creating campaign:', error);
                })
                .finally(() => {
                    setSubmitting(false);
                });
        }
    });

    const { data: dataCatalogues, error: errorCatalogues, isLoading: isLoadingCatalogues } = useGetCataloguesQuery(undefined, { skip: !formik?.values?.isAdvantageCatalogueEnabled });

    // Update smart_promotion_type based on objective
    useEffect(() => {
        if (formik.values.objective === 'OUTCOME_LEADS') {
            formik.setFieldValue('smart_promotion_type', 'MANUAL_LEAD');
        } else if (formik.values.objective === 'OUTCOME_SALES') {
            formik.setFieldValue('smart_promotion_type', 'MANUAL_SALES');
        }   else if (formik.values.objective === 'OUTCOME_APP_PROMOTION') {
            formik.setFieldValue('smart_promotion_type', 'MANUAL_APP_PROMOTION');
        }  else if (formik.values.objective === 'OUTCOME_ENGAGEMENT') {
            formik.setFieldValue('smart_promotion_type', 'MANUAL_ENGAGEMENT');
        } else if (formik.values.objective === 'OUTCOME_TRAFFIC') {
            formik.setFieldValue('smart_promotion_type', 'MANUAL_TRAFFIC');
        }
    }, [formik.values.objective]);

    const handleBudgetChange = (e) => {
        const selectedBudgetType = e.target.value;
        if (selectedBudgetType === 'daily_budget') {
            formik.setFieldValue('lifetime_budget', Number(formik.values.lifetime_budget));
            formik.setFieldValue('daily_budget', Number(formik.values.daily_budget));
        } else if (selectedBudgetType === 'lifetime_budget') {
            formik.setFieldValue('daily_budget', Number(formik.values.daily_budget));
            formik.setFieldValue('lifetime_budget', Number(formik.values.lifetime_budget));
        }
        formik.setFieldValue('budget', selectedBudgetType);
    };

    const handleCatalogueSelect = useCallback((cat) => {
        formik.setFieldValue('product_catalog_id', cat.id);
    }, [formik.setFieldValue]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior='inside' closeOnOverlayClick={false} closeOnEsc={false}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Create Campaign</ModalHeader>
                <ModalCloseButton />

                <ModalBody>
                    <form onSubmit={formik.handleSubmit}>
                        <Box bg={background} p={4} borderRadius="md">
                            <Flex direction="column">

                                {/* Campaign Name */}
                                <Box mb={4}>
                                    <Flex>
                                        <Text mb={2} fontWeight={'bold'}>Campaign Name</Text>
                                        <Text display="inline-flex" fontSize="18px" color="red.400" mb={2}>*&nbsp;</Text>
                                    </Flex>
                                    <Input
                                        placeholder="Enter campaign name"
                                        bg="#fff"
                                        name="name"
                                        size="sm"
                                        borderRadius="md"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.name && formik.errors.name && (
                                        <Text fontSize="12px" color="red.500" mt={1}>{formik.errors.name}</Text>
                                    )}
                                </Box>

                                {/* objective */}
                                <Box mb={4}>
                                    <Flex alignItems={'center'}>
                                        <Text mb={2} fontWeight={'bold'}>Choose a campaign objectives</Text>
                                        <Text display="inline-flex" fontSize="18px" color="red.400" mb={2}>*&nbsp;</Text>
                                        <Tooltip
                                            borderRadius={5}
                                            label={
                                                <Box fontSize="12px" p={'5px'}>
                                                    <Text textAlign={'justify'}>Choose what you want people to do when they see your ads. Your campaign objective should align with your overall business goals.</Text>
                                                </Box>
                                            }
                                            fontSize="md"
                                        >
                                            <InfoOutlineIcon pointerEvents="all" mb={2} />
                                        </Tooltip>
                                    </Flex>
                                    <Select
                                        name="objective"
                                        value={formik.values.objective}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        size="sm"
                                        bg="#fff"
                                        borderRadius="md"
                                    >
                                        <option value="OUTCOME_LEADS">Leads</option>
                                        <option value="OUTCOME_SALES">Sales</option>
                                        <option value="OUTCOME_AWARENESS">Awareness</option>
                                        <option value="OUTCOME_APP_PROMOTION">App promotion</option>
                                        <option value="OUTCOME_ENGAGEMENT">Engagement</option>
                                        <option value="OUTCOME_TRAFFIC">Traffic</option>
                                    </Select>
                                    {formik.touched.objective && formik.errors.objective && (
                                        <Text fontSize="12px" color="red.500" mt={1}>{formik.errors.objective}</Text>
                                    )}
                                </Box>

                                {/* smart_promotion_type */}
                                <Box mb={4}>
                                    <RadioGroup
                                        colorScheme='brand'
                                        value={formik.values.smart_promotion_type}
                                        onChange={(value) => {
                                            formik.setFieldValue('smart_promotion_type', value);
                                        }}
                                        onBlur={formik.handleBlur}
                                    >
                                        <Stack direction="column" spacing={4}>

                                            {/* OUTCOME_LEADS options */}
                                            {/* {formik.values.objective === 'OUTCOME_LEADS' ? (
                                                <Radio value={'MANUAL_LEAD'} size="sm">
                                                    <Flex gap={2}>
                                                        <Text>Manual leads campaign</Text>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={<Box fontSize="12px" p={'5px'}><Text textAlign={'justify'}>Create a leads campaign from scratch for finer control over all settings.</Text></Box>}
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" mt={1} />
                                                        </Tooltip>
                                                    </Flex>
                                                </Radio>
                                            ) : null} */}

                                            {/* OUTCOME_SALES options */}
                                            {formik.values.objective === 'OUTCOME_SALES' && salesOptions?.map((option, index) => {
                                                return (
                                                    <Radio key={index} value={option.value} size="sm">
                                                        <Flex gap={2}>
                                                            <Text>{option.label1}</Text>
                                                            <Tooltip
                                                                borderRadius={5}
                                                                label={<Box fontSize="12px" p={'5px'}><Text textAlign={'justify'}>{option.label2}</Text></Box>}
                                                                fontSize="md"
                                                            >
                                                                <InfoOutlineIcon pointerEvents="all" mt={1} />
                                                            </Tooltip>
                                                        </Flex>
                                                    </Radio>
                                                )
                                            })}

                                            {/* OUTCOME_APP_PROMOTION options */}
                                            {/* {formik.values.objective === 'OUTCOME_APP_PROMOTION' ? (
                                                <Radio value={'MANUAL_APP_PROMOTION'} size="sm">
                                                    <Flex gap={2}>
                                                        <Text>Manual app promotion campaign</Text>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={<Box fontSize="12px" p={'5px'}><Text textAlign={'justify'}>Create an app promotion campaign from scratch using the standard setup.</Text></Box>}
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" mt={1} />
                                                        </Tooltip>
                                                    </Flex>
                                                </Radio>
                                            ) : null} */}

                                            {/* OUTCOME_ENGAGEMENT options */}
                                            {/* {formik.values.objective === 'OUTCOME_ENGAGEMENT' ? (
                                                <Radio value={'MANUAL_ENGAGEMENT'} size="sm">
                                                    <Flex gap={2}>
                                                        <Text>Manual engagement campaign</Text>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={<Box fontSize="12px" p={'5px'}><Text textAlign={'justify'}>Create an engagement campaign from scratch for finer control over all settings.</Text></Box>}
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" mt={1} />
                                                        </Tooltip>
                                                    </Flex>
                                                </Radio>
                                            ) : null} */}

                                            {/* OUTCOME_TRAFFIC options */}
                                            {/* {formik.values.objective === 'OUTCOME_TRAFFIC' ? (
                                                <Radio value={'MANUAL_TRAFFIC'} size="sm">
                                                    <Flex gap={2}>
                                                        <Text>Manual traffic campaign</Text>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={<Box fontSize="12px" p={'5px'}><Text textAlign={'justify'}>Create a traffic campaign from scratch for finer control over all settings.</Text></Box>}
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" mt={1} />
                                                        </Tooltip>
                                                    </Flex>
                                                </Radio>
                                            ) : null} */}
                                        </Stack>
                                    </RadioGroup>
                                </Box>

                                {/* Advantage Catalogue section */}
                                {formik?.values.objective === 'OUTCOME_SALES' && formik?.values?.smart_promotion_type === 'MANUAL_SALES'  ?
                                    <>
                                        <Flex direction='column'>
                                            <Flex alignItems={'center'} justifyContent={'space-between'} mb={2}>
                                                <Box display={'flex'} alignItems={'center'} gap={1}>
                                                    <Text fontWeight="bold">Advantage+ catalogue ads</Text>
                                                    <GiStarShuriken />
                                                </Box>

                                                <Box display={'flex'} alignItems={'center'} gap={2}>
                                                    <Text>{formik?.values.isAdvantageCatalogueEnabled ? 'On' : 'Off'}</Text>
                                                    <Switch colorScheme='brand' isChecked={formik?.values.isAdvantageCatalogueEnabled} onChange={() => formik?.setFieldValue('isAdvantageCatalogueEnabled', !formik.values.isAdvantageCatalogueEnabled)} />
                                                </Box>
                                            </Flex>
                                            <Text fontSize={'12px'} textAlign={'justify'}>Drive sales using your product information by showing relevant products to the right people.</Text>
                                        </Flex>

                                        {formik?.values?.isAdvantageCatalogueEnabled ? 
                                            <Box py={2}>
                                                {isLoadingCatalogues ? (
                                                    <Flex>
                                                        <SkeletonText width="80%" noOfLines={2} skeletonHeight={2} />
                                                    </Flex>
                                                ) : dataCatalogues?.data?.length > 0 ? (
                                                    <Box>
                                                        <CatalogueSelector
                                                            dataCatalogues={dataCatalogues}
                                                            onSelected={handleCatalogueSelect}
                                                        />
                                                    </Box>
                                                ):null}
                                            </Box>
                                        :null}
                                    </>
                                :null}

                                {/* Advantage campaign section */}
                                {formik?.values?.smart_promotion_type === 'AUTOMATED_SHOPPING_ADS' ? null :
                                    <Box>
                                        <Flex alignItems={'center'} justifyContent={'space-between'} mb={2}>
                                            <Box display={'flex'} alignItems={'center'} gap={1}>
                                                <Text fontWeight="bold">Advantage campaign budget</Text>
                                                <GiStarShuriken />
                                            </Box>

                                            <Box display={'flex'} alignItems={'center'} gap={2}>
                                                <Text>{formik.values.isAdvantageCampaignEnabled ? 'On' : 'Off'}</Text>
                                                <Switch colorScheme='brand' isChecked={formik.values.isAdvantageCampaignEnabled} onChange={() => formik.setFieldValue('isAdvantageCampaignEnabled', !formik.values.isAdvantageCampaignEnabled)} />
                                            </Box>
                                        </Flex>
                                        <Text fontSize={'12px'} textAlign={'justify'}>Distribute your budget across ad sets to get more results. You can control spending for each ad set.</Text>
                                    </Box>
                                }

                                {formik.values.isAdvantageCampaignEnabled ? (
                                    <>
                                        <Flex my={2} direction={'column'} gap={2}>
                                            {/* Budget */}
                                            <Flex alignItems={'center'}>
                                                <Text fontWeight="bold">Budget</Text>
                                                <Tooltip
                                                    borderRadius={5}
                                                    label={
                                                        <Box fontSize="sm" p={'5px'}>
                                                            <Text >
                                                                If you choose a daily budget, you may spend more on some days and less on others but will maintain an average daily budget. If you choose a lifetime budget, we'll never spend more than that amount.
                                                            </Text>
                                                        </Box>
                                                    }
                                                    fontSize="md"
                                                >
                                                    <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                </Tooltip>
                                            </Flex>
                                            <Box>
                                                <Flex gap={1}>
                                                    <Select
                                                        name="budget"
                                                        value={formik.values.budget}
                                                        onChange={handleBudgetChange}
                                                        onBlur={formik.handleBlur}
                                                        bg="#fff"
                                                        borderRadius={'md'}
                                                        maxW={'200px'}
                                                        size={'sm'}
                                                    >
                                                        {budgetOptions?.map((option) => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                    {formik.values.budget === 'daily_budget' ? (
                                                        <InputGroup>
                                                            <Input
                                                                name="daily_budget"
                                                                placeholder={currencySymbol}
                                                                bg="#fff"
                                                                type="number"
                                                                value={String(formik.values.daily_budget)}
                                                                onChange={(e) => formik.setFieldValue('daily_budget', e.target.value)}
                                                                onBlur={formik.handleBlur}
                                                                borderRadius={'md'}
                                                                size={'sm'}
                                                            />
                                                            <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                                {currencyCode}
                                                            </InputRightAddon>
                                                        </InputGroup>
                                                    ) : null}
                                                    {formik.values.budget === 'lifetime_budget' ? (
                                                        <InputGroup>
                                                            <Input
                                                                name="lifetime_budget"
                                                                placeholder={currencySymbol}
                                                                bg="#fff"
                                                                type="number"
                                                                value={String(formik.values.lifetime_budget)}
                                                                onChange={(e) => formik.setFieldValue('lifetime_budget', e.target.value)}
                                                                onBlur={formik.handleBlur}
                                                                borderRadius={'md'}
                                                                size={'sm'}
                                                            />
                                                            <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                                {currencyCode}
                                                            </InputRightAddon>
                                                        </InputGroup>
                                                    ) : null}
                                                </Flex>
                                                <Flex>
                                                    {formik.values.budget === 'daily_budget' && formik.touched.daily_budget && formik.errors.daily_budget && (
                                                        <Text fontSize={'12px'} color="red.500">{formik.errors.daily_budget}</Text>
                                                    )}
                                                    {formik.values.budget === 'lifetime_budget' && formik.touched.lifetime_budget && formik.errors.lifetime_budget && (
                                                        <Text fontSize={'12px'} color="red.500">{formik.errors.lifetime_budget}</Text>
                                                    )}
                                                </Flex>
                                            </Box>
                                            <Box display={'flex'} flexDirection='column' gap='2'>
                                                <Flex alignItems={'center'}>
                                                    <Text fontWeight="bold">Campaign bid strategy</Text>
                                                    <Tooltip
                                                        borderRadius={5}
                                                        label={
                                                            <Box fontSize="sm" p={'5px'}>
                                                                <Text >
                                                                    Cost per result goal, ROAS goal or bid caps allow you to enter a specific amount later for each ad set.
                                                                </Text>
                                                            </Box>
                                                        }
                                                        fontSize="md"
                                                    >
                                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                    </Tooltip>
                                                </Flex>

                                                <Flex>
                                                    <Text fontSize={'12px'}>How we'll bid in ad auctions.</Text>
                                                </Flex>

                                                <Flex>
                                                    <Menu>
                                                        <MenuButton
                                                            as={Button}
                                                            borderRadius="md"
                                                            bg='#fff'
                                                            p={2}
                                                            cursor="pointer"
                                                            _hover={{ bg: 'none' }}
                                                            _expanded={{ bg: 'gray.100' }}
                                                            w='auto'
                                                            fontSize='12px'
                                                        >
                                                            <Flex alignItems={'center'} justifyContent="space-between" gap='2'>
                                                                <Text>
                                                                    {formik.values.bid_strategy
                                                                        ? (formik.values.objective === 'OUTCOME_SALES'
                                                                            ? bidStrategiesForSales.find(option => option.value === formik.values.bid_strategy)?.label
                                                                            : formik.values.objective === 'OUTCOME_LEADS'
                                                                            ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label
                                                                            : formik.values.objective === 'OUTCOME_APP_PROMOTION'
                                                                            ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label 
                                                                            : formik.values.objective === 'OUTCOME_AWARENESS'
                                                                            ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label 
                                                                            : formik.values.objective === 'OUTCOME_TRAFFIC'
                                                                            ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label 
                                                                            : formik.values.objective === 'OUTCOME_ENGAGEMENT'
                                                                            ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label 
                                                                            : "Select a bid strategy")

                                                                        : "Select a bid strategy"}
                                                                </Text>
                                                                <ChevronDownIcon boxSize='5' />
                                                            </Flex>
                                                        </MenuButton>

                                                        <MenuList zIndex={20}>
                                                            {(formik.values.objective === 'OUTCOME_SALES' ? bidStrategiesForSales : bidStrategiesForLeads).map((option) => (
                                                                <MenuItem
                                                                    key={option.value}
                                                                    onClick={() => {
                                                                        formik.setFieldValue('bid_strategy', option.value);
                                                                    }}
                                                                    _hover={{ bg: 'gray.50' }}
                                                                    py={2}
                                                                    flexDirection="column"
                                                                    alignItems="flex-start"
                                                                >
                                                                    <Text fontWeight="medium" fontSize="12px">{option.label}</Text>
                                                                    <Text fontSize="12px">
                                                                        {option.description}
                                                                    </Text>
                                                                </MenuItem>
                                                            ))}
                                                        </MenuList>
                                                    </Menu>
                                                </Flex>

                                                <Flex alignItems={'center'}>
                                                    <Text fontSize={'10px'}>Not all bid strategies are available for all campaigns.</Text>
                                                    <Tooltip
                                                        borderRadius={5}
                                                        label={
                                                            <Box fontSize="12px" p={'5px'}>
                                                                <Text >
                                                                    The highest-volume bid strategy is only available if all ad sets are using the same delivery optimisation.<br></br>
                                                                    Cost per result goal is only available for some delivery optimisations.
                                                                </Text>
                                                            </Box>
                                                        }
                                                        fontSize="md"
                                                    >
                                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                    </Tooltip>
                                                </Flex>
                                            </Box>
                                        </Flex>

                                        <Divider />

                                        <Button py={2} justifyContent={'flex-start'} onClick={() => setIsSchedulingExpanded(!isSchedulingExpanded)} variant="link" rightIcon={isSchedulingExpanded ? <ChevronUpIcon color={buttonTextColor} /> : <ChevronDownIcon color={buttonTextColor} />}>
                                            <Text fontSize={'14px'} color={buttonTextColor}>{isSchedulingExpanded ? 'Hide Options' : 'Show More Options'}</Text>
                                        </Button> 

                                        {/* Collapsible content */}
                                        <Collapse in={isSchedulingExpanded}>     
                                            <Flex direction={'column'} gap={4}>
                                                <Flex direction={'column'} gap={2}>
                                                    <Flex direction={'column'}>
                                                        <Text fontWeight="bold">Budget scheduling</Text>
                                                        <Text fontSize={'12px'}>Increase your budget during specific days or times.</Text>
                                                    </Flex>
                                                    <Flex>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            p={2}
                                                            ml={20}
                                                            placement='top'
                                                            fontSize={'12px'}
                                                            label="Your high-demand periods are turned off and will not run until you enable budget scheduling."
                                                        >
                                                            <span>
                                                                <Checkbox
                                                                    colorScheme={"brand"}
                                                                    name="is_budget_schedule_enabled"
                                                                    size={'md'}
                                                                    fontSize={'12px'}
                                                                    isChecked={formik?.values.is_budget_schedule_enabled}
                                                                    onChange={formik?.handleChange}
                                                                    isDisabled={formik?.values.budget !== 'daily_budget'}
                                                                >
                                                                    <Text fontSize='12px'>Schedule budget increases</Text>
                                                                </Checkbox>
                                                            </span>
                                                        </Tooltip>
                                                    </Flex>
                                                    {formik?.values?.is_budget_schedule_enabled && formik?.values?.budget === 'daily_budget' ? 
                                                        <BudgetScheduleSpecs
                                                            specs={formik?.values?.budget_schedule_specs}
                                                            setFieldValue={formik?.setFieldValue}
                                                            currency={adsAccountData?.currency}
                                                        />
                                                    :null}
                                                </Flex> 
                                                <Flex direction='column' gap={1}>
                                                    <Flex alignItems={'center'}>
                                                        <Text fontWeight="bold">Ad scheduling</Text>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={
                                                                <Box fontSize="sm" p={'5px'}>
                                                                    <Text >
                                                                        You can schedule your ads to deliver on specific hours and days of the week based on the viewer's or your ad account's time zone.
                                                                    </Text>
                                                                </Box>
                                                            }
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                        </Tooltip>
                                                    </Flex>
                                                    <Text fontSize='12px'>You'll set the specific schedule within each ad set.</Text>
                                                    <Flex>
                                                        <Checkbox
                                                            size={'md'}
                                                            colorScheme='brand'
                                                            isChecked={formik.values.isAdScheduled}
                                                            onChange={() => formik.setFieldValue('isAdScheduled', !formik.values.isAdScheduled)}
                                                            isDisabled={formik.values.budget !== 'lifetime_budget'}
                                                        >
                                                            <Flex direction='column'>
                                                                <Text fontSize='12px'>Run ads on a schedule</Text>
                                                                {formik.values.budget !== 'lifetime_budget' ?
                                                                    <Text fontSize='10px'>Ad scheduling is only available when you use lifetime budget.</Text>
                                                                    : null}
                                                            </Flex>
                                                        </Checkbox>
                                                    </Flex>
                                                </Flex>

                                                {formik.values.bid_strategy === 'LOWEST_COST_WITH_BID_CAP' ? 
                                                    <Flex direction="column" gap={2}>
                                                        <Flex align={'center'} mt={'10px'}>
                                                            <Text fontWeight="bold">Delivery type</Text>
                                                            <Tooltip
                                                                borderRadius={5}
                                                                label={
                                                                    <Flex direction={'column'} fontSize="12px" p={'5px'}>
                                                                        <Text>Standard delivery uses pacing to control your spend.</Text>
                                                                        <Text>Pacing keeps you from spending your budget too quickly and is the recommended option for most advertisers.</Text>
                                                                        <Text>Accelerated delivery can be useful for time-sensitive promotions. You need to set a bid cap to use accelerated delivery.</Text>
                                                                    </Flex>
                                                                }
                                                                fontSize="md"
                                                            >
                                                                <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                            </Tooltip>
                                                        </Flex>
                                                        {/* Added Checkbox for Accelerated delivery */}
                                                        <Flex gap={2}>
                                                            <Checkbox
                                                                colorScheme={"brand"}
                                                                name="accelerated"
                                                                size={'md'}
                                                                isChecked={formik.values.accelerated}
                                                                onChange={formik.handleChange}
                                                            />
                                                            <Flex direction="column">
                                                                <Text fontSize="14px" fontWeight="bold">Accelerated</Text>
                                                                <Text fontSize="12px">Spend your budget and get results as quickly as possible</Text>
                                                            </Flex>
                                                        </Flex>
                                                    </Flex>
                                                :null}
                                            </Flex>
                                        </Collapse>
                                    </>
                                ) : (null)}
                            </Flex>
                        </Box>
                    </form>
                </ModalBody>

                {/* Modal Footer with the submit button */}
                <ModalFooter gap={2} justifyContent={'flex-start'}>
                    <Button
                        variant="solid"
                        colorScheme="gray"
                        size={'sm'}
                        onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme="brand"
                        isDisabled={!formik.isValid}
                        isLoading={formik.isSubmitting}
                        type="submit"
                        size="sm"
                        onClick={formik.handleSubmit}
                    >
                        Continue
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default CreateCampaignForm;
